'use client';

import clsx from 'clsx';
import Image from 'next/image';
import HubSpotForm from './HubSpotForm';
import { useTranslation } from '@/hooks/useTranslation';
import { BAND_SIZES } from '@/lib/responsiveImage';
import s from './BookDemoSection.module.css';

/*
 * BookDemoSection — "See Locobuzz In Action" demo band.
 *
 * Redesigned to the reference: a dark rounded band with the headline +
 * green-check benefit bullets + Trusted & Certified badges on the left, the
 * HubSpot demo form (in a white card) on the right, and an optional 3D robot
 * image bleeding off the right edge (/public/robot-hand-locobuzzz.webp).
 *
 * The form itself is unchanged — <HubSpotForm> still embeds the HubSpot form
 * (formName "book-a-demo") and pushes the generate_lead / ECL dataLayer event.
 * Used across the site (homepage, ai-stack pages, etc.).
 *
 * formEager — when true, HubSpot v2.js preloads immediately. Default false
 * so below-the-fold demo bands don't compete with LCP.
 */

const CERTS = [
  { src: '/certs/soc2.webp', alt: 'SOC 2 Type 2' },
  { src: '/certs/iso27001.webp', alt: 'ISO 27001' },
  { src: '/certs/gdpr.png', alt: 'GDPR Compliant' },
  { src: '/certs/tac-security.png', alt: 'TAC Security ESOF Verified' },
];

export default function BookDemoSection({ id = 'book-demo', variant = 'dark', formEager = false }) {
  const { t } = useTranslation();
  const isLight = variant === 'light';

  return (
    <section id={id} className={clsx(s.section, isLight && s.sectionLight)}>
      <div className={clsx(s.band, isLight && s.bandLight)}>
        {!isLight && (
        <Image
          className={s.robot}
          src="/robot-hand-locobuzzz.webp"
          alt=""
          aria-hidden="true"
          width={960}
          height={960}
          sizes={BAND_SIZES}
          draggable={false}
          loading="lazy"
          fetchPriority="low"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        )}

        <div className={s.inner}>
          {/* ── Left ── */}
          <div className={s.lhs}>
            <h2 className={clsx(s.title, isLight && s.titleLight)}>
              {t('demo.see_in_action_pre')}{' '}
              <span className={clsx(s.titleBrand, isLight && s.titleBrandLight)}>{t('demo.locobuzz')}</span>{' '}
              <span className={clsx('serif-accent', s.titleSerif, isLight && s.titleSerifLight)}>{t('demo.see_in_action_post')}</span>
            </h2>

            <ul className={s.list}>
              {[t('demo.b1'), t('demo.b2'), t('demo.b3'), t('demo.b4')].map((item) => (
                <li key={item} className={clsx(s.item, isLight && s.itemLight)}>
                  <svg className={s.tick} width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <circle cx="10" cy="10" r="10" fill="#27C98A" />
                    <polyline points="6,10.5 9,13.5 14,7" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className={s.certs}>
              <span className={clsx(s.certsLabel, isLight && s.certsLabelLight)}>{t('Trusted & Certified')}</span>
              <div className={s.certsRow}>
                {CERTS.map((c) => (
                  <span key={c.src} className={s.badge}>
                    <img
                      src={c.src}
                      alt={c.alt}
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right — HubSpot form ── */}
          <div className={clsx(s.rhs, isLight && s.rhsLight)}>
            <div className={clsx(s.card, isLight && s.cardInset)}>
              <h3 className={s.cardTitle}>{t('Schedule a personalized demo')}</h3>
              <p className={s.cardSub}>{t('Our team will get in touch with you shortly')}</p>
              <HubSpotForm formName="book-a-demo" eager={formEager} />
              <p className={s.privacyNote}>
                Personal data will be processed in accordance with Locobuzz’s{' '}
                <a href="/privacy-policy/" className={s.privacyLink}>Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
